<template>
  <div class="neo-hero">
    <img class="neo-hero__bg" :src="getWebsiteImage('neo-bg.png')" alt="BG" />
    <div class="neo-container">
      <neo-nav :show-only-routes="true" />
      <div class="neo-other--header">
        <div class="title" data-aos="fade-up">
          <h1>Terms of Service</h1>
        </div>
        <div class="description">
          <p data-aos="fade-up">Version: 1.00 - Last updated: January 2024</p>
        </div>
      </div>
    </div>
  </div>
  <div class="neo-container">
    <div class="neo-terms">
      <section>
        <h5>1. Introduction</h5>
        <ul>
          <li>
            1.1 These terms govern your interaction with the 99Fi app (“App”),
            our website, and the diverse range of services provided by us
            (“Services”).
          </li>
          <li>
            1.2. "Neo," “99Fi”, "us," or "we," refers to NEO LLC, known as NEO
            FS in the UK, a pioneering company in financial technology focusing
            on credit and savings solutions.
          </li>

          <li>
            1.3. NEO LLC manages the [website url] website and the Neo app,
            dedicated to enhancing your financial health through various
            services, including an array of credit products.
          </li>

          <li>
            1.4. NEO LLC, with a significant operational presence in the United
            Kingdom, US, and Canada, is committed to offering top-tier financial
            solutions.
          </li>

          <li>
            1.5. In relevant jurisdictions, NEO serves as an Authorized
            Representative for partners such as XYZ Limited, which is regulated
            in these regions. XYZ Limited, recognized for its financial
            expertise, is registered and authorized (Reference number: [insert
            reference number]) to provide services in:
            <ul>
              <li>Credit facilitation</li>
              <li>Credit information services</li>
              <li>Consumer lending</li>
            </ul>
          </li>
          <li>
            1.6. For verification of financial regulatory compliance, you are
            encouraged to consult the Financial Services Register available on
            the FCA website.
          </li>

          <li>
            1.7. In adherence to data protection legislation, Neo LLC, operating
            as NEO FS in the UK, is responsible for managing the personal data
            we collect (ICO Registration Number: [insert number]).
          </li>

          <li>
            1.8. Should you have inquiries regarding these terms, feel free to
            reach out to us. You can contact us via email at
            <strong><a href="mailto:support@withneo.co">support@withneo.co</a></strong>
          </li>
        </ul>
      </section>
      <section>
        <h5>2. Services and Subscription Overview</h5>
        <ul>
          <li>
            2.1. App Features: Neo's App offers both complimentary and premium
            subscription-based features. Detailed pricing and subscription
            service information are readily accessible within the App and on our
            website. All specifics are clearly outlined when you make a
            purchase.
          </li>

          <li>
            2.2. Subscription Cancellation: You have the flexibility to cancel
            your subscription at any time, but we ask for a 30-day notice. Your
            subscription will officially end after the current billing period.
          </li>

          <li>
            2.3. Subscription Management: We handle subscriptions on an ongoing
            basis. Rest assured, we will keep you updated about renewal dates or
            the conclusion of your subscription term well in advance.
          </li>

          <li>
            2.4. Non-Subscription Use: If you choose not to subscribe, you're
            welcome to continue using the free features of the App whenever you
            like, with no obligations.
          </li>

          <li>
            2.5. Scope of Services: Please note, Neo's services focus on
            financial technology, not on credit repair, legal, tax, or financial
            consulting. While we aim to help improve your financial situation,
            we cannot guarantee changes in credit scores, as these depend on
            several factors beyond our services. Remember, Neo is not a credit
            bureau.
          </li>

          <li>
            2.6. Financial Advice Disclaimer: Neo does not provide investment or
            consumer credit advice. For important financial decisions, we
            strongly advise consulting with a professional advisor who is
            authorized by the Financial Conduct Authority.
          </li>

          <li>
            2.7. Technical Information: For comprehensive details on the
            technical functionality and guidelines for using our App, please
            refer to our app store listings or feel free to contact us directly
            for any queries.
          </li>
        </ul>
      </section>
      <section>
        <h5>3. Terms Related to Financial Services</h5>
        <p>
          Neo provides a range of financial services, including Credit Accounts
          and Neo Credit Cards, partnering with various Financial Institutions.
          When you apply for these services through our platform and website, we
          forward your account information to the corresponding Financial
          Institution. If your application is approved, this information helps
          establish distinct accounts for each service with the relevant
          Financial Institution.
        </p>

        <p>
          You will be required to sign specific agreements with both Neo and the
          Issuing Financial Institution for each financial service you choose.
          This typically includes a credit agreement and promissory note for
          Credit Accounts, as well as a cardholder agreement for Neo Credit
          Cards.
        </p>

        <ul>
          <li>
            3.1. Credit Agreement Activation: When you apply for credit through
            Neo's App, a separate contract detailing the terms will come into
            effect once your request is approved.
          </li>

          <li>
            3.2. Approval Communication: Neo will notify you through the App
            about the outcome of your credit application. If you need to make
            changes or cancel before approval, please contact us quickly using
            the communication options available in the App.
          </li>

          <li>
            3.3. Requirement for Additional Information: During the processing
            of your credit application or throughout your subscription period,
            we may need further details from you. If extra information is
            required, we will get in touch. Failure to provide necessary
            information promptly may lead to the rejection of your application.
          </li>

          <li>
            3.4. Managing the Credit Agreement: The termination process and the
            rights regarding cancellation under the Credit Agreement are
            governed according to the terms specified in the agreement.
          </li>

          <li>
            3.5. Licensing and App Access: Access to the Neo App and its
            services is granted under a license for personal use. All
            intellectual property rights associated with the App and services
            remain the property of Neo.
          </li>
        </ul>
      </section>
      <section>
        <h5>4. Neo Credit Reporting Services Terms</h5>
        <p>Understanding Credit Reporting Services</p>

        <p>
          By agreeing to these Terms, you provide "written instructions" under
          the Fair Credit Reporting Act (“FCRA”) to credit reporting agencies
          like Experian, TransUnion, Equifax, and our third-party service
          providers to access and exchange your credit information. This
          facilitates identity verification and service provision.
        </p>

        <p>
          You authorize these Consumer Report Providers to disclose your
          personal information as per our Privacy Policy and Privacy Notice. You
          also release any Consumer Report Providers from liability for
          third-party actions related to the use and disclosure of this
          information.
        </p>

        <p>
          These services, including credit score and monitoring, are contingent
          on validation by the applicable Consumer Report Provider. During
          enrollment, you'll be asked for various details to verify your
          identity and facilitate service provision, including contact and
          financial information.
        </p>

        <p>
          You agree to utilize these services responsibly, primarily for
          protecting against fraud and unauthorized activities.
        </p>

        <h6>Credit Data Furnishing</h6>
        <p>
          Neo, along with any relevant Issuing Financial Institutions, reports
          your financial activities like credit repayments and bill payments to
          credit reporting agencies (CRAs). This aids in building your credit
          history and is governed by CRA guidelines and the FCRA or associated
          financial authority in the jurisdiction you are accessing our services
          from.
        </p>

        <p>
          Neo and its Issuing Financial Institution are not liable for how CRAs
          manage or use the information provided. For detailed terms on how CRAs
          handle such information, please contact them directly. Once data is
          transmitted to a CRA, they may hold an ownership interest in that
          data.
        </p>
      </section>
      <section>
        <h5>5. Credit Score and Report Access</h5>
        <ul>
          <li>
            5.1. To access your Credit Scores and Report, a successful
            authentication process is required, involving sharing your data with
            CRAs like TransUnion, Experian, and Equifax. Not everyone passes
            this process; one reason might be the inability to match your
            details with the correct credit profile.
          </li>

          <li>
            5.2. Your request for a Credit Score and Report should be strictly
            for personal, non-commercial use. Requesting third-party credit
            information is not permitted.
          </li>

          <li>
            5.3. CRAs strive to ensure accuracy in the Credit Scores and
            Reports, but complete accuracy isn't guaranteed. If inaccuracies are
            found, you should contact the CRAs directly.
          </li>

          <li>
            5.4. Intellectual property rights in the CRAs' Credit Score and
            Report belong to the CRAs and/or their licensors.
          </li>

          <li>
            5.5. Requesting a Credit Report leaves a non-visible footprint to
            lenders but visible to you, the CRAs, and authorized third parties.
          </li>

          <li>
            5.6. For queries or disputes regarding your Score and Report,
            contact us through our customer support. We may direct your query to
            the relevant CRA.
          </li>

          <li>
            5.7. Information in the Score and Report is for guidance only. A
            high credit score does not guarantee acceptance by financial service
            providers, as they have separate criteria, including affordability
            assessments.
          </li>

          <li>
            5.8. The full Credit Report might not be entirely displayed; only
            pertinent information will be provided for your guidance.
          </li>
        </ul>
      </section>
      <section>
        <h5>6. Contact Information</h5>
        <ul>
          <li>
            6.1. For assistance or inquiries, please contact Neo through our
            dedicated email support at [insert Neo's email address] or via the
            messaging service within our App. We ensure to maintain effective
            communication with you, either through the App or via the email
            address and contact details you have provided us. Please be aware
            that all our Terms and correspondence are exclusively provided in
            English for clarity and consistency.
          </li>
        </ul>
      </section>
      <section>
        <h5>7. Privacy, Data Use, and Personal Information</h5>
        <ul>
          <li>
            7.1. At Neo, we prioritize your privacy and data protection. The
            personal information you provide to us is processed in accordance
            with our comprehensive Privacy Policies. By agreeing to these Terms,
            you confirm that you have thoroughly read, understood, and accepted
            both our Privacy Policy and Privacy Notice.
          </li>

          <li>
            7.2. It is essential for the accuracy and currency of your personal
            details. This includes, but is not limited to, your name, email
            address, date of birth, residential address, employment status,
            monthly savings, income, debt responsibilities, and bank account
            information. Please ensure the prompt updating of any changes in
            your personal information.
          </li>

          <li>
            7.3. Neo operates as an integrated financial technology platform,
            offering a range of both proprietary and third-party services. When
            you engage with our Platform or use our Services, you acknowledge
            and consent to our collection and utilization of your data. This
            encompasses information you directly provide as well as data sourced
            on your behalf from third parties, including financial institutions
            whose services are integrated with our Platform.
          </li>

          <li>
            7.4. In case of any concerns regarding the security or privacy of
            your Neo account, please contact us immediately. We are available
            via email at [insert Neo's email address] or through the in-app
            messaging service. Our commitment to addressing your concerns
            promptly and efficiently is a testament to our dedication to your
            data security and privacy.
          </li>
        </ul>
      </section>
      <section>
        <h5>
          8. Agreement to Terms and User Obligations for Neo Services Acceptance
          of Terms
        </h5>
        <p>
          By registering and using the Neo Platform, you agree to these Terms of
          Service. Your acceptance is necessary to access and use the Platform
          and to qualify for our Services, as outlined below. By using the
          Platform, you authorize us to share your data within our Platform for
          service provision, eligibility assessment, marketing of offerings, and
          other lawful purposes, as detailed in our Privacy Policies. This data
          may include credit information and other details obtained from third
          parties.
        </p>
        <h6>Access and Usage Conditions</h6>
        <p>
          By accessing and/or using the Platform, you acknowledge and agree to:
        </p>
        <ul>
          <li>
            Adhere to all terms and conditions set forth in these Terms of
            Service, including the Neo Online Privacy Policy and the
            Gramm-Leach-Bliley Privacy Notice, collectively referred to as the
            “Privacy Policies”.
          </li>
          <li>Confirm that you are at least 18 years of age.</li>
          <li>Verify your capability to form a binding contract with Neo.</li>
          <li>
            Confirm your residence in the jurisdiction where Neo operates, and
            at the address you have or will provide to us.
          </li>
          <li>
            Ensure you are not restricted from using the Platform or Services
            under the laws of your jurisdiction.
          </li>
        </ul>
        <h6>User Responsibilities for Neo Services</h6>
        <ul>
          <li>
            8.1. Eligibility: You must be at least 18 years old to use Neo's App
            or Services and must reside in an area where Neo operates.
          </li>

          <li>
            8.2. User Conduct:
            <ul>
              <li>
                8.2.1. Use Neo's App and Services exclusively for personal use.
              </li>
              <li>
                8.2.2. Prohibit the renting, leasing, sublicensing, loaning, or
                unauthorized distribution of the App or Services.
              </li>
              <li>
                8.2.3. Avoid any illegal use of the App or Services that could
                cause harm or infringe on the rights of Neo or third parties.
              </li>
              <li>
                8.2.4. Do not engage in activities that could disrupt, modify,
                or cause damage to the App or Services.
              </li>
            </ul>
          </li>

          <li>
            8.3. Security: Keep your login credentials secure and confidential
            to prevent unauthorized account access.
          </li>

          <li>
            8.4. Termination Rights: We may terminate your access to the App or
            Services if you violate these Terms or are suspected of such
            violation.
          </li>

          <li>
            8.5. Continual Obligations: Termination of these Terms for any
            reason does not release you from any existing liabilities or
            obligations, such as repayments under any credit agreements
            established with Neo.
          </li>
        </ul>
        <p>
          By agreeing to these terms, you acknowledge and accept your role in
          using Neo’s services responsibly, while also understanding our
          commitment to maintaining a secure and lawful platform.
        </p>
      </section>
      <section>
        <h5>9. Limitation of Liability for Neo Services</h5>
        <p>
          In no event shall Neo, its affiliates, officers, directors, employees,
          agents, or third-party providers be liable for any indirect, direct,
          incidental, punitive, special, or consequential damages. This
          includes, but is not limited to, damages for loss of profits, data, or
          usage arising from or connected to this agreement, the use or
          performance of the Neo Platform, Site, Services, or Content. This also
          covers delays, inability to access or use the Platform, Site, or
          Services, provision or failure to provide services, or any content,
          software, products, and services obtained through the Platform, Site,
          or Content. This applies whether the liability is based on contract,
          tort, negligence, strict liability, or other grounds, even if Neo has
          been informed of the possibility of such damages.
        </p>
      </section>
      <section>
        <h5>10. Indemnification</h5>
        <p>
          You agree to indemnify and hold harmless Neo, its affiliates,
          third-party vendors, directors, officers, employees, and agents from
          any claims, damages, or suits arising from:
        </p>
        <ul>
          <li>
            (i) Your breach or alleged breach of these Terms, including any
            infringement or alleged infringement of any third party's copyright
            or intellectual property rights.
          </li>
          <li>
            (ii) Your fraudulent or malicious use of the Services, or misuse or
            abuse of the Services.
          </li>
          <li>
            (iii) Your violation or alleged violation of any applicable law,
            rule, or regulation in your use of the Services.
          </li>
          <li>
            (iv) Neo's use of materials generated or uploaded by you in
            accordance with these Terms.
          </li>
        </ul>

        <p>
          By accepting these terms, you acknowledge the limits of Neo's
          liability and your responsibility to protect Neo against certain types
          of legal action related to your use of the Services.
        </p>
      </section>
      <section>
        <h5>11. Complaints & Compensation</h5>
        <ul>
          <li>
            11.1. If you have any concerns or issues with Neo's App or Services,
            please feel free to raise a complaint directly with us. You can do
            so by writing to us at [insert Neo's email for complaints] or via
            our in-app messaging service.
          </li>

          <li>
            11.2. In cases where our services fall under the regulatory scope of
            the Financial Conduct Authority and you're unable to resolve your
            complaint with us, you may have the right to refer it to the
            Financial Ombudsman Service. The Financial Ombudsman Service's
            contact details are:<br />

            Financial Ombudsman Service<br />
            Exchange Tower<br />
            London E14 9SR<br />
            Telephone: 0800 023 4567<br />
            Website: www.financial-ombudsman.org.uk
          </li>
        </ul>
      </section>
      <section>
        <h5>12. Jurisdiction</h5>

        <ul>
          <li>
            12.1. These Terms of Service are provided in English and are
            governed by and construed under the laws of the jurisdiction where
            Neo operates. For instance, in the UK, they are governed by English
            law, or Scottish law if you are domiciled in Scotland. Any disputes
            related to these Terms will be subject to the exclusive jurisdiction
            of the courts in the area of your domicile, whether that be in
            England, Wales, Scotland, or Northern Ireland, as applicable.
          </li>
        </ul>
        <p>
          Please note, jurisdictional and regulatory specifics can vary based on
          the country in which Neo operates and the relevant local laws in
          effect.
        </p>
      </section>
    </div>
  </div>
  <neo-footer />
</template>

<script>
import NeoNav from "@/components/Website/NeoNav";
import NeoFooter from "@/components/Website/NeoFooter";
export default {
  name: "NeoTermsOfService",
  components: { NeoFooter, NeoNav },
};
</script>

<style lang="scss" scoped>
.neo-hero {
  height: 100%;
  padding-bottom: 80px;
}

.neo-other--header {
  width: 80%;
  margin: 0 auto;
  text-align: center;

  .title {
    margin: 50px 0 10px;
  }

  .description {
    p {
      margin: 0 auto 10px;
    }
  }
}

.neo-terms {
  width: 75%;
  margin: 0 auto;
  padding: 50px 0;

  h5 {
    margin-bottom: 20px;
  }

  section {
    margin-bottom: 30px;

    h6 {
      font-size: 1.25rem;
      margin-bottom: 15px;
    }
    p {
      font-size: 1rem;
      color: #121212;
      opacity: 1;
      margin-bottom: 15px;
    }

    > ul {
      list-style-type: none;
      margin: 0 0 15px;
      padding: 0;

      li {
        color: #121212 !important;
        line-height: 1.5;

        &:not(:last-child) {
          margin-bottom: 15px;
        }
      }

      ul {
        margin: 15px 0;

        li:not(:last-child) {
          margin-bottom: 10px;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .neo-other--header {
    .description p {
      font-size: 1rem;
    }
  }

  .neo-terms {
    width: 90%;
  }
}

@media (max-width: 600px) {
  .neo-hero {
    height: 100%;
    padding-bottom: 20px;
  }

  .neo-other--header {
    width: 100%;
    .title {
      margin: 20px 0;

      h1 {
        font-size: 2rem;
      }
    }

    .description p {
      text-align: justify;
    }
  }

  .neo-terms {
    width: 100%;
    padding: 30px 0;
  }
}
</style>
